import { Fragment, type ComponentProps, type ReactElement } from 'react'
import type { SalesOrder, SalesOrderDocument } from '@/lib/services/sales-orders/sales-orders.types'
import type { NumericInput } from '@/lib/utils'
import type { OrderProductRow } from './order-product-rows'
import type { SalesOrderPaymentLedger } from './sales-order-payments'
import type { usePathHelper } from '@/hooks/usePathHelper'
import type { getDiscountRows } from './SalesOrderMoneyPresentation'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, Flower2, Route as RouteIcon } from 'lucide-react'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { cn, formatCurrency, parseCurrency } from '@/lib/utils'
import {
  DetailWell,
  DetailWorkspaceOverview,
  DetailWorkspacePanel,
  DetailWorkspaceTab,
  DetailWorkspaceTabs,
  DetailWorkspaceTabsList,
} from '@/components/patterns/detail-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { DeliveryAttemptTimeline } from './DeliveryAttemptTimeline'
import { CondolencesProductsFinancialSummary } from './SalesOrderMoneyPresentation'
import OrderItemProductCard from './OrderItemProductCard'
import DiscountCodeSummaryLink from './DiscountCodeSummaryLink'
import { ShopFeeTag } from '@/components/fees/ShopFeeTag'
import { PAYMENT_TONES, SalesOrderPaymentLines } from './SalesOrderPaymentLines'

type ProductCardProps = ComponentProps<typeof OrderItemProductCard>
type WorkspaceOrder = Pick<
  SalesOrder,
  'fulfillment_type' | 'delivery_attempt_count' | 'latest_delivery_attempt' | 'delivery_attempts'
>
type DeliveryRoutes = ReturnType<typeof usePathHelper>['deliveryRoutes']
type DeliveryRoutesLink =
  | ReturnType<DeliveryRoutes['show']>['link']
  | ReturnType<DeliveryRoutes['index']>['link']

interface WorkspacePayment extends SalesOrderPaymentLedger {
  guidance?: string | null
  onCollectPayment?: (() => void) | null
  onEditTerms?: (() => void) | null
}

// Display accepts the owner's decimal strings and historical numeric summaries; it never totals them.
type WorkspaceInvoice = {
  [Key in keyof Pick<
    SalesOrderDocument,
    'subtotal' | 'variable_service_fee' | 'variable_service_fee_tax_amount' | 'tax_amount' | 'total'
  >]?: NumericInput
}

export interface SalesOrderProductSummary {
  condolences?: ComponentProps<typeof CondolencesProductsFinancialSummary>['summary'] | null
  useAlternateSurface?: boolean
  invoice?: WorkspaceInvoice | null
  discounts: ReturnType<typeof getDiscountRows>
  freightFee: number
  showDeliveryFeeFallback?: boolean
  deliveryFeeFallbackTooltip?: string
  relayFee: number
  tipAmount: number
  shopFee: number
  shopFeeLabel?: string | null
  tax: number
  floristTax: number
  total: number
  payment?: WorkspacePayment | null
}

interface DeliveryPresentation {
  showSection: boolean
  activeRouteLabel?: string | null
  routesLink: DeliveryRoutesLink
  actionLabel: string
}

interface ProductsPanelProps {
  lineItems: readonly OrderProductRow<ProductCardProps['item']>[]
  onViewItemDetails: ProductCardProps['handleViewItemDetailsClick']
  summary: SalesOrderProductSummary
}

// Where the order stands on payment, beside the totals: paid, balance due, refunded, or
// cancelled, with the actions that fit that state.
function SalesOrderPaymentStatus({ payment }: { payment: WorkspacePayment }): ReactElement {
  const tone = PAYMENT_TONES[payment.status.tone] ?? PAYMENT_TONES.neutral

  return (
    <Card
      data-testid="sales-order-payment-status"
      data-tone={payment.status.tone}
      size="flush"
      surface={tone.surface}
      rail={tone.rail}
      className="w-full overflow-hidden @[36rem]/order-totals:max-w-[22rem]"
    >
      <CardContent className="space-y-2 p-4 pl-5">
        <Typography variant="eyebrow">Payment</Typography>
        <Typography variant="h4" tone={tone.text} wrap="balance" className="pb-0">
          {payment.status.headline}
        </Typography>
        {payment.status.detail ? (
          <Typography variant="supporting" className="block">
            {payment.status.detail}
          </Typography>
        ) : null}
        {payment.guidance ? (
          <Typography variant="caption" className="block">
            {payment.guidance}
          </Typography>
        ) : null}
        {payment.onCollectPayment || payment.onEditTerms ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {payment.onCollectPayment ? (
              <Button size="sm" onClick={payment.onCollectPayment}>
                Collect payment
              </Button>
            ) : null}
            {payment.onEditTerms ? (
              <Button size="sm" variant="outline" onClick={payment.onEditTerms}>
                Edit terms
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function SalesOrderProductsPanel({
  lineItems,
  onViewItemDetails,
  summary,
}: ProductsPanelProps): ReactElement {
  const variableServiceFee = parseCurrency(summary.invoice?.variable_service_fee)
  const variableServiceFeeTax = parseCurrency(summary.invoice?.variable_service_fee_tax_amount)
  // The shop fee its payments were charged, and Tax, the florist's tax and the Order total, all as the API totals them.
  const { shopFee, tax, floristTax } = summary

  return (
    <DetailWorkspacePanel value="products">
      <div className="min-w-0">
        <ul role="list" className="divide-y divide-border/60">
          {lineItems.map(
            (
              { item, key, relationship, quantity, lineQuantity, lineUnitOffset, isReturned },
              rowIndex
            ) => (
              <li
                data-testid={
                  relationship === 'child'
                    ? 'sales-order-child-line-item'
                    : 'sales-order-product-row'
                }
                data-row-key={key}
                data-relationship={relationship}
                key={key}
                className={cn(rowIndex % 2 === 1 && 'bg-elevated')}
              >
                {relationship === 'child' ? (
                  <div className="relative ml-8 pl-8 sm:ml-12 sm:pl-10">
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-0 h-8 w-px bg-border/70"
                    />
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-8 h-px w-6 bg-border/70"
                    />
                    <OrderItemProductCard
                      item={item}
                      quantity={quantity}
                      lineQuantity={lineQuantity}
                      lineUnitOffset={lineUnitOffset}
                      isReturned={isReturned}
                      relationship="child"
                      handleViewItemDetailsClick={onViewItemDetails}
                    />
                  </div>
                ) : (
                  <OrderItemProductCard
                    item={item}
                    quantity={quantity}
                    lineQuantity={lineQuantity}
                    lineUnitOffset={lineUnitOffset}
                    isReturned={isReturned}
                    handleViewItemDetailsClick={onViewItemDetails}
                  />
                )}
              </li>
            )
          )}
        </ul>

        {summary.condolences ? (
          <CondolencesProductsFinancialSummary
            summary={summary.condolences}
            useAlternateSurface={summary.useAlternateSurface}
          />
        ) : !summary.invoice && summary.payment ? (
          <div className="@container/order-totals flex flex-col gap-4 border-t border-border/60 px-4 py-4 sm:px-5">
            <SalesOrderPaymentStatus payment={summary.payment} />
            <div className="w-full max-w-[22rem] self-end">
              <SalesOrderPaymentLines payment={summary.payment} />
            </div>
          </div>
        ) : summary.invoice ? (
          <div
            data-testid="sales-order-products-summary"
            data-surface-tone={summary.useAlternateSurface ? 'alternate' : 'default'}
            className={cn(
              '@container/order-totals border-t border-border/60 px-4 py-4 sm:px-5',
              summary.useAlternateSurface && 'bg-elevated'
            )}
          >
            <div className="flex flex-col gap-5 @[36rem]/order-totals:flex-row @[36rem]/order-totals:items-start @[36rem]/order-totals:justify-between">
              {summary.payment ? <SalesOrderPaymentStatus payment={summary.payment} /> : null}
              <div className="w-full max-w-[22rem] space-y-2 self-end @[36rem]/order-totals:self-start">
                <div className="grid grid-cols-[1fr_auto] gap-x-5 gap-y-1 text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-right tabular-nums">
                    {formatCurrency(summary.invoice.subtotal)}
                  </span>
                  {summary.discounts.map((discount) => (
                    <Fragment key={discount.id}>
                      <span className="min-w-0 text-muted-foreground">
                        <DiscountCodeSummaryLink
                          code={discount.code}
                          discountId={discount.discountId}
                          fallbackLabel={discount.label}
                        />
                      </span>
                      <span className="text-right tabular-nums">
                        {formatCurrency(discount.amount)}
                      </span>
                    </Fragment>
                  ))}
                  {summary.freightFee > 0 ? (
                    <>
                      <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground">
                        Delivery
                        {summary.showDeliveryFeeFallback ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  role="img"
                                  aria-label={summary.deliveryFeeFallbackTooltip}
                                  className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-status-warning-foreground ring-1 ring-status-warning-border dark:text-status-warning-foreground dark:ring-status-warning-border"
                                >
                                  <AlertTriangle className="size-3" aria-hidden="true" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" align="start" className="max-w-xs">
                                {summary.deliveryFeeFallbackTooltip}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : null}
                      </span>
                      <span className="text-right tabular-nums">
                        {formatCurrency(summary.freightFee)}
                      </span>
                    </>
                  ) : null}
                  {variableServiceFee > 0 ? (
                    <>
                      <Typography variant="bodySmall" tone="muted">
                        Service Fee
                      </Typography>
                      <Typography variant="bodySmall" align="right" className="tabular-nums">
                        {formatCurrency(variableServiceFee)}
                      </Typography>
                    </>
                  ) : null}
                  {shopFee > 0 ? (
                    <>
                      <Typography variant="bodySmall" tone="muted">
                        {summary.shopFeeLabel || 'Shop fee'} <ShopFeeTag />
                      </Typography>
                      <Typography variant="bodySmall" align="right" className="tabular-nums">
                        {formatCurrency(shopFee)}
                      </Typography>
                    </>
                  ) : null}
                  <span className="text-muted-foreground">Tax</span>
                  <span className="text-right tabular-nums">{formatCurrency(tax)}</span>
                  {variableServiceFee > 0 ? (
                    <>
                      <Typography variant="bodySmall" tone="muted" className="pl-3">
                        Sales tax
                      </Typography>
                      <Typography
                        variant="bodySmall"
                        tone="muted"
                        align="right"
                        className="tabular-nums"
                      >
                        {formatCurrency(floristTax)}
                      </Typography>
                      <Typography variant="bodySmall" tone="muted" className="pl-3">
                        Tax collected for OneStem
                      </Typography>
                      <Typography
                        variant="bodySmall"
                        tone="muted"
                        align="right"
                        className="tabular-nums"
                      >
                        {formatCurrency(variableServiceFeeTax)}
                      </Typography>
                    </>
                  ) : null}
                  {summary.relayFee > 0 ? (
                    <>
                      <span className="text-muted-foreground">Relay Fee</span>
                      <span className="text-right tabular-nums">
                        {formatCurrency(summary.relayFee)}
                      </span>
                    </>
                  ) : null}
                  {summary.tipAmount > 0 ? (
                    <>
                      <span className="text-muted-foreground">Tip</span>
                      <span className="text-right tabular-nums">
                        {formatCurrency(summary.tipAmount)}
                      </span>
                    </>
                  ) : null}
                </div>

                <div className="border-t border-border/60 pt-2">
                  <div className="grid grid-cols-[1fr_auto] gap-x-5 text-sm">
                    <span className="font-medium text-foreground">Total</span>
                    <span className="text-right font-semibold tabular-nums text-foreground">
                      {formatCurrency(summary.total)}
                    </span>
                  </div>
                </div>
                {summary.payment ? <SalesOrderPaymentLines payment={summary.payment} /> : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </DetailWorkspacePanel>
  )
}

function SalesOrderDeliveryPanel({
  order,
  presentation,
}: {
  order: WorkspaceOrder
  presentation: DeliveryPresentation
}): ReactElement {
  return (
    <DetailWorkspacePanel value="deliveries">
      <div>
        <DetailWorkspaceOverview
          icon={RouteIcon}
          title="Delivery timeline"
          description="Delivery attempts, missed-delivery notes, proof photos, and route activity for this order."
        >
          <div className="flex flex-wrap items-center gap-2 @[70rem]/summary:justify-end">
            <Badge variant="outline">
              {order.delivery_attempt_count || 0} attempt
              {order.delivery_attempt_count === 1 ? '' : 's'}
            </Badge>
            {order.latest_delivery_attempt?.summary_label ? (
              <Badge variant="brand">{order.latest_delivery_attempt.summary_label}</Badge>
            ) : null}
          </div>
        </DetailWorkspaceOverview>

        <div className="space-y-4 px-4 py-4 sm:px-5 sm:py-5">
          {presentation.activeRouteLabel ? (
            <DetailWell
              compact
              tone="periwinkle"
              className="flex flex-wrap items-center justify-between gap-4"
            >
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Typography variant="h5" className="flex items-center gap-2">
                    <RouteIcon className="size-4" />
                    Delivery Routes
                  </Typography>
                  <Badge variant="brand">{presentation.activeRouteLabel}</Badge>
                </div>
                <Typography variant="supporting">
                  This order is already assigned to an active route. Open the route to copy the
                  link, view the stops, or revoke that route before building another one.
                </Typography>
              </div>

              <Button variant="outline" size="sm" asChild>
                <Link {...presentation.routesLink}>{presentation.actionLabel}</Link>
              </Button>
            </DetailWell>
          ) : null}

          {order.fulfillment_type === FulfillmentType.Delivery.Value ||
          order.delivery_attempt_count > 0 ? (
            <DeliveryAttemptTimeline
              attempts={order.delivery_attempts || []}
              emptyMessage="No delivery attempts logged yet."
              showRoute
            />
          ) : null}
        </div>
      </div>
    </DetailWorkspacePanel>
  )
}

export function SalesOrderWorkspace({
  value,
  onValueChange,
  order,
  lineItems,
  onViewItemDetails,
  productSummary,
  deliveryPresentation,
}: {
  value?: ComponentProps<typeof DetailWorkspaceTabs>['value']
  onValueChange?: ComponentProps<typeof DetailWorkspaceTabs>['onValueChange']
  order: WorkspaceOrder
  lineItems: ProductsPanelProps['lineItems']
  onViewItemDetails: ProductsPanelProps['onViewItemDetails']
  productSummary: SalesOrderProductSummary
  deliveryPresentation: DeliveryPresentation
}): ReactElement {
  return (
    <DetailWorkspaceTabs value={value} onValueChange={onValueChange}>
      <div className="min-w-0">
        <DetailWorkspaceTabsList>
          <DetailWorkspaceTab value="products" icon={Flower2}>
            Products
          </DetailWorkspaceTab>
          {deliveryPresentation.showSection ? (
            <DetailWorkspaceTab value="deliveries" icon={RouteIcon}>
              Deliveries
            </DetailWorkspaceTab>
          ) : null}
        </DetailWorkspaceTabsList>
      </div>

      <SalesOrderProductsPanel
        lineItems={lineItems}
        onViewItemDetails={onViewItemDetails}
        summary={productSummary}
      />

      {deliveryPresentation.showSection ? (
        <SalesOrderDeliveryPanel order={order} presentation={deliveryPresentation} />
      ) : null}
    </DetailWorkspaceTabs>
  )
}
